import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import SelectAilment from "components/Questionnaire/steps/SelectAilment";
import GeneralHealth from "components/Questionnaire/steps/GeneralHealth";
import BasicInfo from "components/Questionnaire/steps/BasicInfo";
import SymptomReview from "components/Questionnaire/steps/SymptomReview";
import ShareTongueImage from "components/Questionnaire/steps/ShareTongueImage";
import { sendAilmentResponse } from "services/postApis";
import { uploadFileToS3 } from "services/aws";
import { ErrorHandler } from "utils/toast";
import "../styles/Questionnaire.css";

const Questionnaire = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});

  // Scroll to top whenever the step changes so the next section starts in view
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Handle browser back button
  useEffect(() => {
    const handleBackButton = () => {
      if (currentStep > 1) {
        // Go to previous step
        setCurrentStep((prev) => prev - 1);
      }
      // If currentStep === 1, allow natural back behavior (user exits questionnaire)
    };

    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [currentStep]);

  const steps = [
    { number: 1, title: "Select Ailment", component: SelectAilment },
    { number: 2, title: "General Health", component: GeneralHealth },
    { number: 3, title: "Basic Information", component: BasicInfo },
    { number: 4, title: "Symptom Review", component: SymptomReview },
    { number: 5, title: "Photo for Diagnosis", component: ShareTongueImage },
  ];

  //Send Ailment Response Api
  const { mutateAsync: mutateAilmentResponseAsync } = useMutation({
    mutationFn: sendAilmentResponse,
    onSuccess: (data) => {
      console.log("Response Result", data);
      navigate("/herbal-plan", {
        state: { userSubmissionId: data?.payload?.id },
        replace: true,
      });
    },
    onError: (err) => {
      ErrorHandler(err);
    },
  });

  const handleContinue = async (stepValue) => {
    console.log("Step Value", stepValue);
    if (stepValue) {
      setFormData((prev) => ({
        ...prev,
        [currentStep]: stepValue,
      }));
    }

    if (currentStep === 5) {
      // Step 5 is the tongue image upload step
      // stepValue here is the File object
      try {
        // Upload image to S3
        const uploadResult = await uploadFileToS3(stepValue);
        console.log("Image uploaded to S3:", uploadResult);

        // Extract just the filename from the key (e.g., "public/tongue/name_123_abc.jpg" -> "name_123_abc.jpg")
        const fileName = uploadResult.key.split("/").pop();

        const mergedData = [2, 4].flatMap((stepNumber) =>
          Object.entries(formData[stepNumber]).map(([key, value]) => ({
            ailmentQuestionId: key,
            response: value,
          })),
        );

        const response = {
          ailmentId: formData[1],
          basicInformation: formData[3],
          answers: mergedData,
          tongueImgURL: fileName,
        };
        console.log("Merged Data", response);
        await mutateAilmentResponseAsync(response);
      } catch (error) {
        console.error("Error during submission:", error);
        ErrorHandler(error);
        throw error; // Re-throw so ShareTongueImage knows it failed
      }
      return;
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      // Push history entry when moving forward so back button can navigate between steps
      window.history.pushState(null, "", window.location.pathname);
    }
  };

  const renderStepContent = () => {
    const step = steps.find((s) => s.number === currentStep);
    const StepComponent = step.component;
    const props = {
      onContinue: handleContinue,
    };
    if (currentStep !== 1) {
      props.ailmentId = formData[1];
    }

    if (StepComponent) {
      return <StepComponent {...props} />;
    }

    return null;
  };

  return (
    <div className="questionnaire-container">
      <div className="progress-section">
        <div className="steps-container">
          {steps
            .filter((s) => s.number <= 5)
            .map((step) => {
              const status =
                currentStep === step.number
                  ? "active"
                  : currentStep > step.number
                    ? "completed"
                    : "inactive";
              return (
                <div key={step.number} className="step-item">
                  <div className={`step-circle ${status}`}>
                    {currentStep > step.number ? "✓" : "⚫"}
                  </div>
                  <span className={`step-title ${status}`}>{step.title}</span>
                </div>
              );
            })}
        </div>
      </div>

      <div
        className="questionnaire-content"
        style={currentStep === 4 ? { maxWidth: "800px" } : {}}
      >
        <div className="content-card">{renderStepContent()}</div>
      </div>
    </div>
  );
};

export default Questionnaire;
